import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GitBranch, Plus, RefreshCw } from "lucide-react";
import {
  MAX_GROOMING_REPOS,
  repoHostLabel,
  type GroomingRepoHost,
  type GroomingRepoInput,
  type GroomingRepoStatus,
  type GroomingRunnerEvent,
} from "@ttc-spectree/toolbox-contract";
import { Chip, type ChipTone } from "@/components/grooming/grooming-chips";
import { RunnerFallback } from "@/components/grooming/runner-fallback";
import { GroomingSwitch, RepoLockLine } from "@/components/grooming/repos/grooming-switch";
import { GitHubRepoForm } from "@/components/grooming/repos/github-repo-form";
import { AddRepoRow } from "@/components/grooming/repos/repo-picker";
import {
  PROJECT_GHOST_ACTION as ghostAction,
  PROJECT_PRIMARY_BUTTON as primaryButton,
  PROJECT_TABLE_HEAD as tableHead,
} from "@/components/grooming/project/project-styles";
import { ErrorLine } from "@/components/toolbox/error-line";
import { Button } from "@/components/ui/button";
import { useSaveGroomingConfig, useSetGroomingEnabled } from "@/hooks/queries/use-grooming";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import { ApiError } from "@/lib/api/client";
import type { GroomingConfig } from "@/lib/api/grooming";
import { getApiErrorCode } from "@/lib/api/work-items";
import { hasGitHubRepo, repoHostOf } from "@/lib/grooming/repo-host";
import { useRunnerBridge, useRunnerEvents } from "@/lib/grooming/runner-bridge";
import { setupStepPath } from "@/lib/toolbox/page-names";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * The Project page's Repositories tab (PKG-PM-F, feature PI-SPEC-SW-1510):
 * the old Repos page's Code tab, moved unchanged under Project →
 * Repositories (`/grooming/:project/project/repositories`). The page itself
 * is `pages/grooming/project.tsx`; the Product areas tab moved to
 * `components/grooming/project/product-areas-tab.tsx`. The history below is
 * the Repos page's.
 *
 * Repos (Project Managers and Admins, `map-project`; epic PI-SPEC-SW-E279, task PI-SPEC-SW-1390-4;
 * restyled to the v2 desktop design in epic PI-SPEC-SW-E280, task
 * PI-SPEC-SW-1405-1: one "Repos" header with the Code / Product areas
 * segmented control, a lead line with the actions beside it, and plain
 * tables. Same content, actions and API calls as before).
 *
 * - Code (Claude Design "All Screens" frame 2h): the repositories Claude
 *   reads for the project, saved in SpecTree and shared by everyone who
 *   grooms it. "Your copy" and its status come only from SpecTree Toolbox on
 *   this computer; SpecTree never knows about a person's local copies, so a
 *   browser shows "—".
 * - Product areas ("Final PM" frame 5c): the plain names every summary uses.
 *   Claude drafts them from the code once (Redraft from code, app only; the
 *   browser shows the slash command); people edit them from then on.
 *
 * The route is wrapped in RequireToolboxCapability; the API enforces the same
 * rule (403 TOOLBOX_CAPABILITY_REQUIRED).
 *
 * In a contract-3 app (epic PI-SPEC-SW-E284) Redraft from code adds a draft
 * to the app's run queue and shows its "Queued · #n" / "Running" badge, and
 * Update my copies is disabled while the queue runs anything for this
 * project, because those runs read the copies (the app refuses too).
 *
 * Epic PI-SPEC-SW-E288, feature PI-SPEC-SW-1467 (UX-PM-43, 03-evaluation.md
 * section 8.6 Q7; "Getting Started Mockups" frames 1e and 1f): the Code tab
 * is locked to the Admin (`manage-repos`; the API enforces it).
 * - Admin: "Add repository" opens a row with repository and branch pickers
 *   read from ADO (no typing), and a visible Grooming on/off switch replaces
 *   the silent turn-on: a config save never sends `enabled`.
 * - Project Manager: read-only, with the lock line naming the Admins, "Update
 *   my copies", and "Sign in to the repos" on the row that needs it.
 * - A failed git step shows the engine's plain reason and what still works;
 *   a rejected refresh or sign-in is shown, not dropped: since feature
 *   PI-SPEC-SW-1512 (UX-PM-16, G4) as an ErrorLine with Try again and ×
 *   that stays until it is closed or the same action succeeds.
 * Product areas keep their `map-project` behaviour unchanged.
 *
 * Epic PI-SPEC-SW-E318, feature PI-SPEC-SW-1679 (ER-1683 Resolved Decision
 * 2): the Admin also gets "Add a GitHub repository", a form where they paste
 * the repository's address (`GitHubRepoForm`; SpecTree never calls GitHub).
 * Every row names its host ("Azure DevOps" / "GitHub", `repoHostLabel`) on
 * the line under the name; a GitHub row shows `owner/repo` there. The
 * MAX_GROOMING_REPOS cap counts both hosts, and a GitHub save, like an ADO
 * one, never sends `enabled`. A GitHub copy that needs a sign-in offers
 * "Sign in to GitHub", which opens Setup's Project code step (Resolved
 * Decision 1); ADO rows keep "Sign in to the repos".
 */

/** Plain reason Update my copies is disabled while queued runs read the project's copies (E284). */
export const COPIES_IN_USE_REASON = "Runs are reading these copies. Update them when the queue has no run for this project.";

/** The Code tab's columns (frames 1e and 1f): repository, branch, your copy, actions. */
const repoGrid = "grid grid-cols-[minmax(0,1.2fr)_130px_minmax(0,1.8fr)_190px] items-center gap-4";

// ---------------------------------------------------------------------------
// Code tab
// ---------------------------------------------------------------------------

/** A repository reference as the config, the status and the form carry it. */
interface RepoRef {
  project?: string | undefined;
  repo: string;
  branch?: string | undefined;
  /** Absent = Azure DevOps (E318). */
  host?: GroomingRepoHost | undefined;
  /** GitHub organisation, for a GitHub entry. */
  owner?: string | undefined;
}

interface RepoStatusView {
  /** The meta line under the chip when nothing went wrong ("Updated 2:12 PM today"). */
  copy: string;
  label: string;
  tone: ChipTone;
  /** Git's reason in plain words, when a step failed (`auth-required` or `error`). */
  reason?: string;
  /** What still works despite the failure ("Your last copy is from …; briefs still use it."). */
  stillWorks?: string;
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? `${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} today`
    : date.toLocaleDateString();
}

/** When a copy is from, for "Your last copy is from …": "today at 2:12 PM", "yesterday" or a date. */
function copyDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return `today at ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "yesterday";
  return date.toLocaleDateString();
}

/** What still works after a failed git step: the last copy, when there is one. */
function stillWorks(status: GroomingRepoStatus): string | undefined {
  return status.lastFetchedAt ? `Your last copy is from ${copyDate(status.lastFetchedAt)}; briefs still use it.` : undefined;
}

/** A GitHub copy's sign-in reason when the app gave none (E318); the ADO default is unchanged. */
export const GITHUB_SIGN_IN_REASON = "GitHub asked you to sign in again. Sign in to GitHub on Setup.";

/**
 * The status chip and the lines under it for one repository's local copy.
 * A failed git step shows the engine's plain `message` and what still works,
 * never a bare chip (UX-PM-43, frame 1f). `host` picks the sign-in words
 * when the app gave no message (E318).
 */
function statusView(status: GroomingRepoStatus | undefined, host: GroomingRepoHost = "ado"): RepoStatusView {
  if (!status) return { copy: "Not copied yet", label: "Not copied yet", tone: "none" };
  const updated = status.lastFetchedAt ? `Updated ${formatTime(status.lastFetchedAt)}` : "Not copied yet";
  switch (status.state) {
    case "ready":
      return { copy: updated, label: "Up to date", tone: "ok" };
    case "auth-required": {
      const view: RepoStatusView = {
        copy: updated,
        label: "Sign-in needed",
        tone: "warn",
        reason:
          status.message ?? (host === "github" ? GITHUB_SIGN_IN_REASON : "Azure DevOps asked you to sign in to the repositories again."),
      };
      const works = stillWorks(status);
      if (works) view.stillWorks = works;
      return view;
    }
    case "missing":
      return { copy: "Not copied yet", label: "Not copied yet", tone: "none" };
    case "cloning":
      return { copy: "Copying…", label: "Updating", tone: "cur" };
    case "fetching":
      return { copy: "Updating…", label: "Updating", tone: "cur" };
    case "error": {
      const view: RepoStatusView = {
        copy: updated,
        label: "Could not update",
        tone: "red",
        reason: status.message ?? "Git could not update this copy.",
      };
      const works = stillWorks(status);
      if (works) view.stillWorks = works;
      return view;
    }
  }
}

function sameRepo(a: RepoRef, b: RepoRef, project: string): boolean {
  return (
    a.repo.toLowerCase() === b.repo.toLowerCase() &&
    (a.project ?? project).toLowerCase() === (b.project ?? project).toLowerCase()
  );
}

/** The plain reason a bridge call was rejected (Electron prefixes "Error invoking remote method '…': Error: "). */
function rejectionReason(error: unknown): string {
  const raw = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  const plain = raw.replace(/^Error invoking remote method '[^']*':\s*/, "").replace(/^(?:[A-Za-z]*Error):\s*/, "").trim();
  if (!plain) return "SpecTree Toolbox did not say why.";
  return /[.!?…]$/.test(plain) ? plain : `${plain}.`;
}

/** Which copies action ran: "Update my copies" or "Sign in to the repos". */
type RepoRunAction = "refresh" | "sign-in";

/** A rejected refresh or sign-in, kept until it is closed or the action succeeds. */
interface RepoRunError {
  action: RepoRunAction;
  message: string;
}

/** The ErrorLine title for a rejected copies action (UX-PM-16, G4). */
export const REPO_RUN_ERROR_TITLES: Readonly<Record<RepoRunAction, string>> = {
  refresh: "Updating your copies didn't finish.",
  "sign-in": "Signing in to the repos didn't finish.",
};

/** The local copies' state from SpecTree Toolbox, kept current by repo-status events. */
function useRepoStatus(project: string) {
  const { runner, support } = useRunnerBridge();
  const [statuses, setStatuses] = useState<GroomingRepoStatus[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [runError, setRunError] = useState<RepoRunError | null>(null);

  useEffect(() => {
    if (!runner) return undefined;
    let active = true;
    void runner.getRepoStatus(project).then(
      (result) => {
        if (active) setStatuses(result);
      },
      () => {
        if (active) setStatuses([]);
      }
    );
    return () => {
      active = false;
    };
  }, [runner, project]);

  const onEvent = useCallback(
    (event: GroomingRunnerEvent) => {
      if (event.type === "repo-status" && event.project.toLowerCase() === project.toLowerCase()) {
        setStatuses(event.repos);
      }
    },
    [project]
  );
  useRunnerEvents(runner, onEvent);

  // A rejected refresh or sign-in is shown, never dropped (UX-PM-43), and stays on
  // screen until it is closed or the same action succeeds (UX-PM-16, G4; frame F3).
  const run = (kind: RepoRunAction) => {
    if (!runner) return;
    setBusy(true);
    const action = kind === "refresh" ? runner.refreshRepos(project) : runner.signInToRepos(project);
    void action.then(
      (result) => {
        setStatuses(result);
        // Only the same action's success clears its error: a refresh does not fix a failed sign-in.
        setRunError((current) => (current?.action === kind ? null : current));
        setBusy(false);
      },
      (error: unknown) => {
        setRunError({ action: kind, message: rejectionReason(error) });
        setBusy(false);
      }
    );
  };

  return {
    runner,
    support,
    statuses,
    busy,
    runError,
    refresh: () => {
      run("refresh");
    },
    signIn: () => {
      run("sign-in");
    },
    retryRun: () => {
      if (runError) run(runError.action);
    },
    clearRunError: () => {
      setRunError(null);
    },
  };
}

function toInput(repo: RepoRef): GroomingRepoInput {
  if (repo.host === "github") {
    const github: GroomingRepoInput = { host: "github", owner: repo.owner ?? "", repo: repo.repo };
    if (repo.branch !== undefined) github.branch = repo.branch;
    return github;
  }
  const input: GroomingRepoInput = { repo: repo.repo };
  if (repo.project !== undefined) input.project = repo.project;
  if (repo.branch !== undefined) input.branch = repo.branch;
  return input;
}

/** True while the run queue has a running entry for this project (its runs read the repo copies). */
function useQueueReadingCopies(project: string): boolean {
  const { state } = useGroomingQueue();
  const projectKey = project.trim().toLowerCase();
  return state?.running.some((entry) => entry.project.trim().toLowerCase() === projectKey) ?? false;
}

/** The plain reason the config was not saved. */
function saveErrorText(error: unknown): string {
  if (getApiErrorCode(error) === "TOOLBOX_CAPABILITY_REQUIRED") return "Only an Admin can change the repositories.";
  return "The repositories were not saved. Try again in a moment.";
}

/**
 * Why a GitHub repository was not saved: the server's own sentence for a
 * refused entry (400, e.g. "SpecTree can read GitHub repositories from … only."),
 * word for word; otherwise the page's plain reason.
 */
function githubSaveErrorText(error: unknown): string {
  if (getApiErrorCode(error) === "TOOLBOX_CAPABILITY_REQUIRED") return saveErrorText(error);
  if (error instanceof ApiError && error.response.status === 400 && error.message.trim().length > 0) return error.message;
  return saveErrorText(error);
}

/** The line under a repository's name: its host, then where it lives ("GitHub · TTC-SpecTree/SpecTree"). */
function repoWhere(repo: RepoRef & { cloneUrl?: string | undefined }, project: string): { host: string; path: string } {
  const host = repoHostOf(repo);
  return {
    host: repoHostLabel(host),
    path: host === "github" ? `${repo.owner ?? ""}/${repo.repo}` : `${repo.project ?? project} / ${repo.repo}`,
  };
}

/** The plain reason the Grooming switch did not change. */
function switchErrorText(error: unknown): string {
  const code = getApiErrorCode(error);
  if (code === "TOOLBOX_CAPABILITY_REQUIRED") return "only an Admin can do that.";
  if (code === "GROOMING_CONFIG_NOT_FOUND") return "the project has no repositories yet.";
  return "SpecTree did not answer. Try again in a moment.";
}

/** The "Your copy" cell: the chip, and under it the time, or git's reason and what still works. */
function CopyCell({ view }: { view: RepoStatusView | null }) {
  if (!view) {
    return (
      <span className="text-sm text-muted-foreground" data-testid="repo-copy">
        —
      </span>
    );
  }
  return (
    <div className="flex min-w-0 flex-col items-start gap-1.5">
      <Chip tone={view.tone} testId="repo-status">
        {view.label}
      </Chip>
      {view.reason ? (
        <p className={cn(TOOLBOX_TEXT.content, "leading-normal text-muted-foreground")} data-testid="repo-reason">
          {view.reason}
          {view.stillWorks ? ` ${view.stillWorks}` : ""}
        </p>
      ) : (
        <span className={cn(TOOLBOX_TEXT.meta, "text-muted-foreground")} data-testid="repo-copy">
          {view.copy}
        </span>
      )}
    </div>
  );
}

/**
 * Project → Repositories (`map-project`): the repositories Claude reads for
 * the project. Admins (`manage-repos`) add and remove them and turn grooming
 * on or off; a Project Manager sees them read-only with the lock line.
 */
export function RepositoriesTab({
  project,
  config,
  notSetUp,
  canManage,
}: {
  project: string;
  config: GroomingConfig | undefined;
  notSetUp: boolean;
  /** `manage-repos` (Admin only, E288): add and remove repositories, the Grooming switch. */
  canManage: boolean;
}) {
  const navigate = useNavigate();
  const save = useSaveGroomingConfig(project);
  const setEnabled = useSetGroomingEnabled(project);
  const { runner, support, statuses, busy, runError, refresh, signIn, retryRun, clearRunError } = useRepoStatus(project);
  const copiesInUse = useQueueReadingCopies(project);
  const [adding, setAdding] = useState(false);
  const [addingGitHub, setAddingGitHub] = useState(false);
  const repos = config?.repos ?? [];
  const atMax = repos.length >= MAX_GROOMING_REPOS;
  const maxReason = atMax ? `A project reads at most ${String(MAX_GROOMING_REPOS)} repositories.` : undefined;
  const anyGitHub = addingGitHub || hasGitHubRepo(repos);

  // The page never sends `enabled`: saving repositories never turns grooming on (E288).
  const saveRepos = (next: GroomingRepoInput[], onDone?: () => void) => {
    save.mutate(
      {
        repos: next,
        teamName: config?.teamName ?? null,
        groomTag: config && !config.groomTagIsDefault ? config.groomTag : null,
      },
      { onSuccess: () => onDone?.() }
    );
  };

  const showUpdate = runner !== null && repos.length > 0;

  return (
    <div className="flex flex-col gap-3.5" data-testid="repos-code">
      {(showUpdate || canManage) && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {showUpdate && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy || copiesInUse}
              title={copiesInUse ? COPIES_IN_USE_REASON : undefined}
              onClick={refresh}
            >
              <RefreshCw className={cn(busy && "animate-spin")} aria-hidden="true" />
              {busy ? "Updating…" : "Update my copies"}
            </Button>
          )}
          {canManage && (
            <Button
              type="button"
              size="sm"
              className={primaryButton}
              disabled={adding || addingGitHub || atMax}
              title={maxReason}
              onClick={() => {
                save.reset();
                setAdding(true);
              }}
            >
              <Plus aria-hidden="true" />
              Add repository
            </Button>
          )}
          {canManage && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={adding || addingGitHub || atMax}
              title={maxReason}
              onClick={() => {
                save.reset();
                setAddingGitHub(true);
              }}
            >
              <Plus aria-hidden="true" />
              Add a GitHub repository
            </Button>
          )}
        </div>
      )}

      {canManage ? (
        <GroomingSwitch
          project={project}
          enabled={config?.enabled ?? false}
          disabledReason={config ? undefined : "Add the first repository, then turn grooming on."}
          pending={setEnabled.isPending}
          error={setEnabled.isError ? switchErrorText(setEnabled.error) : null}
          onChange={(enabled) => {
            setEnabled.mutate(enabled);
          }}
        />
      ) : (
        <RepoLockLine project={project} enabled={config?.enabled} />
      )}

      {runError && (
        <div data-testid="repos-run-error">
          <ErrorLine
            title={REPO_RUN_ERROR_TITLES[runError.action]}
            cause={runError.message}
            onRetry={() => {
              if (!busy) retryRun();
            }}
            onClose={clearRunError}
          />
        </div>
      )}
      {runner && repos.length > 0 && copiesInUse && (
        <p className="text-[13px] text-muted-foreground" role="status" data-testid="copies-in-use">
          {COPIES_IN_USE_REASON}
        </p>
      )}

      {notSetUp && (
        <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} data-testid="repos-not-set-up">
          {canManage
            ? `${project} is not set up for grooming yet. Add its first repository, then turn grooming on.`
            : `${project} is not set up for grooming yet. An Admin adds its repositories.`}
        </p>
      )}

      {save.isError && !addingGitHub && (
        <p className={cn(TOOLBOX_TEXT.content, "text-destructive")} role="alert">
          {saveErrorText(save.error)}
        </p>
      )}

      {(repos.length > 0 || adding || addingGitHub) && (
        <div className={cn("glass-card rounded-[10px]", adding || addingGitHub ? "overflow-visible" : "overflow-x-auto")}>
          <div className="min-w-[720px]">
            <div className={cn(repoGrid, tableHead)}>
              <span>{canManage && !anyGitHub ? "Repository (from Azure DevOps)" : "Repository"}</span>
              <span>Branch</span>
              <span>Your copy</span>
              <span />
            </div>
            <ul>
              {repos.map((repo) => {
                const status = statuses?.find((s) => sameRepo(s, repo, project));
                const github = repoHostOf(repo) === "github";
                const view = runner ? statusView(status, github ? "github" : "ado") : null;
                const needsSignIn = runner !== null && status?.state === "auth-required";
                const where = repoWhere(repo, project);
                return (
                  <li
                    key={`${repo.project ?? project}/${repo.repo}`}
                    className={cn(repoGrid, "border-b border-[#141417] px-[18px] py-3 last:border-b-0", view?.reason && "items-start")}
                    data-testid="repo-row"
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="flex min-w-0 items-center gap-2 font-medium">
                        <GitBranch className={cn("h-4 w-4 shrink-0", TOOLBOX_TEXT.faint)} aria-hidden="true" />
                        <span className="truncate">{repo.repo}</span>
                      </span>
                      <span className={cn("truncate font-mono text-xs", TOOLBOX_TEXT.faint)} data-testid="repo-where">
                        <span data-testid="repo-host">{where.host}</span> · {where.path}
                      </span>
                    </div>
                    <span className="font-mono text-xs text-muted-foreground">{repo.branch ?? "default"}</span>
                    <CopyCell view={view} />
                    <span className="flex flex-wrap justify-end gap-1">
                      {needsSignIn && github && (
                        // GitHub's sign-in is on Setup (ER-1683 Resolved Decision 1), not "Sign in to the repos".
                        <Button
                          type="button"
                          size="sm"
                          className={primaryButton}
                          onClick={() => {
                            void navigate(setupStepPath("project-code"));
                          }}
                        >
                          Sign in to GitHub
                        </Button>
                      )}
                      {needsSignIn && !github && (
                        <Button type="button" size="sm" className={primaryButton} disabled={busy} onClick={signIn}>
                          Sign in to the repos
                        </Button>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          className={ghostAction}
                          disabled={repos.length <= 1 || save.isPending}
                          title={repos.length <= 1 ? "A project needs at least one repository." : undefined}
                          onClick={() => {
                            saveRepos(repos.filter((r) => !sameRepo(r, repo, project)).map(toInput));
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </span>
                  </li>
                );
              })}
              {canManage && adding && (
                <AddRepoRow
                  project={project}
                  gridClassName={repoGrid}
                  pending={save.isPending}
                  onCancel={() => {
                    setAdding(false);
                  }}
                  onSave={(repo) => {
                    if (repos.some((r) => sameRepo(r, repo, project))) {
                      setAdding(false);
                      return;
                    }
                    saveRepos([...repos.map(toInput), repo], () => {
                      setAdding(false);
                    });
                  }}
                />
              )}
              {canManage && addingGitHub && (
                <GitHubRepoForm
                  project={project}
                  repos={repos}
                  allowedOwners={config?.allowedGitHubOwners}
                  pending={save.isPending}
                  saveError={save.isError ? githubSaveErrorText(save.error) : null}
                  onCancel={() => {
                    save.reset();
                    setAddingGitHub(false);
                  }}
                  onSave={(repo) => {
                    saveRepos([...repos.map(toInput), repo], () => {
                      setAddingGitHub(false);
                    });
                  }}
                />
              )}
            </ul>
          </div>
        </div>
      )}

      {!runner && repos.length > 0 && <RunnerFallback support={support} />}

      <p className={cn("text-[13px] leading-normal", TOOLBOX_TEXT.faint)}>
        Each person's computer keeps its own read-only copy and updates it before every batch. Nothing is ever pushed
        back. Other ADO projects appear in the project menu once an Admin sets up their repositories here and turns
        grooming on.
      </p>
    </div>
  );
}
